// The Square's motion: everything that moves on its own, so the world feels
// alive even when nobody else is online.
//
//   wind         gusts roll across the map; trees, shrubs and reeds bend with
//                them (the controller skews their sprites by wind(x, y))
//   water        ducks paddle the pond, ripples spread, a fish jumps now and
//                then, the sun glints, lily pads bob
//   fire & home  smoke curls from the campfire and the cottage chimney,
//                embers and sparks rise from the fire
//   creatures    butterflies over the flowers by day, birds crossing with
//                their shadows, fireflies after dark
//   places       music notes drift from Jane Radio, a film flickers on the
//                big screen, the stage's spotlights sweep, string lights
//                twinkle
//   light        cloud shadows drift over the meadow; a light-theme map
//                follows the viewer's clock through dawn, golden hour, dusk
//                and night (the dark theme is always night)
//   walkers      dust kicks up behind feet; a puff marks every arrival
//
// With prefers-reduced-motion everything holds still: no wind, no drifting,
// no particles — the scene is drawn at rest.
import { rng, noise2, line } from "./paint"
import { NorthAmbient } from "./ambient_north"
import { SouthAmbient } from "./ambient_south"

const TAU = Math.PI * 2

export class Ambient {
    constructor(world, { seed = 1, reduceMotion = false } = {}) {
        this.world = world
        this.reduce = reduceMotion
        this.r = rng(seed + 404)
        this.n = noise2(seed + 405)
        this.t = 0
        this.smoke = []
        this.embers = []
        this.notes = []
        this.dust = []
        this.poofs = []
        this.ripples = []
        this.glints = []
        this.fish = null
        this.nextFish = 6
        this.birds = null
        this.nextBirds = 8
        const pond = world.pond
        this.ducks = [0, 1].map((i) => ({ a: this.r() * TAU, speed: 0.07 + i * 0.03, rx: pond.rx * (0.55 - i * 0.15), ry: pond.ry * (0.5 - i * 0.12), bob: this.r() * TAU, color: i ? "brown" : "white" }))
        const spots = world.meadowSpots.length ? world.meadowSpots : [{ x: world.W / 2, y: world.H / 2 }]
        const area = Math.max(1, (world.W * world.H) / (2000 * 1250))
        this.area = area
        this.butterflies = Array.from({ length: Math.round(8 * area) }, (_, i) => {
            const from = spots[Math.floor(this.r() * spots.length)]
            return { x: from.x, y: from.y, tx: from.x, ty: from.y, phase: this.r() * TAU, color: ["#F2C14E", "#F39A5B", "#B7A2E6", "#8FC7E8", "#FFFFFF"][i % 5], rest: this.r() * 3 }
        })
        this.spots = spots
        const treeSpots = world.trees.filter((_, i) => i % 3 === 0).map((t) => ({ x: t.x, y: t.y - t.s }))
        const glowSpots = [...treeSpots, ...spots, { x: pond.x, y: pond.y - pond.ry }]
        this.fireflies = Array.from({ length: Math.round(46 * area) }, () => {
            const s = glowSpots[Math.floor(this.r() * glowSpots.length)]
            return { hx: s.x, hy: s.y, x: s.x, y: s.y, phase: this.r() * TAU, speed: 0.6 + this.r() * 0.8, blink: 0.8 + this.r() * 1.6 }
        })
        this.clouds = Array.from({ length: Math.round(5 * area) }, () => ({ x: this.r() * world.W, y: this.r() * world.H, s: 260 + this.r() * 260, squash: 0.45 + this.r() * 0.25 }))
        // the koi in the zen garden, and the harbour's rowboats
        this.koi = Array.from({ length: 4 }, (_, i) => ({ a: this.r() * TAU, speed: 0.25 + this.r() * 0.2, k: 0.45 + i * 0.12, color: ["#F08A3E", "#FFFFFF", "#E8543A", "#F6C84A"][i] }))
        this.boats = (world.anchors.boats || []).map((b, i) => ({ ...b, phase: this.r() * TAU, color: ["#C8453A", "#3E6FB0", "#E8B43A"][i % 3] }))
        this.view = { x: 0, y: 0, w: world.W, h: world.H }
        // sheep in the paddock, each drifting toward a spot and grazing there
        const pad = world.anchors.paddock
        this.sheep = pad ? Array.from({ length: 7 }, () => {
            const a = this.r() * TAU, d = Math.sqrt(this.r())
            const x = pad.x + Math.cos(a) * pad.rx * d, y = pad.y + Math.sin(a) * pad.ry * d
            return { x, y, tx: x, ty: y, graze: this.r() * 4, facing: this.r() < 0.5 ? -1 : 1, bob: this.r() * TAU }
        }) : []
        this.sailboats = (world.anchors.sailboats || []).map((b) => ({ ...b }))
        this.glow = makeGlow()
        // the Northern Reach moves in its own module, layered into each pass
        this.north = new NorthAmbient(world, { seed, reduceMotion, glow: this.glow })
        this.south = new SouthAmbient(world, { seed, reduceMotion, glow: this.glow })
        this.cloudCanvas = makeCloud()
    }

    // --- wind -------------------------------------------------------------------

    // How far upright things bend at (x, y) right now, as a skew factor:
    // a steady lean downwind, a slow gust rolling west to east, and a
    // quicker flutter that differs from tree to tree.
    wind(x, y) {
        if (this.reduce) return 0
        const t = this.t
        const gust = 0.5 + 0.5 * Math.sin(t * 0.35 - x * 0.0035 + y * 0.001)
        const flutter = Math.sin(t * 1.7 + x * 0.05 + y * 0.03) * 0.6 + Math.sin(t * 2.9 + x * 0.09) * 0.4
        return 0.008 + gust * gust * 0.02 + flutter * (0.006 + gust * 0.018)
    }

    windDrift() {
        if (this.reduce) return 0
        return 8 + 14 * (0.5 + 0.5 * Math.sin(this.t * 0.35))
    }

    // --- the clock ----------------------------------------------------------------

    // The light for this moment: `tint` is multiplied over the whole scene
    // (null in full daylight), `lamps` is how brightly lamps and windows
    // glow (0..1), `creatures` says who is out.
    light(night, hour) {
        if (night) return { tint: null, lamps: 1, day: false }
        if (hour >= 5 && hour < 7) return { tint: ["255,190,170", 0.16], lamps: 0.35, day: true }
        if (hour >= 7 && hour < 17) return { tint: null, lamps: 0, day: true }
        if (hour >= 17 && hour < 19) return { tint: ["255,196,120", 0.16], lamps: 0.2, day: true }
        if (hour >= 19 && hour < 21) return { tint: ["150,140,210", 0.26], lamps: 0.7, day: false }
        return { tint: ["80,100,170", 0.32], lamps: 1, day: false }
    }

    // --- updates -------------------------------------------------------------------

    // Called when a walker is moving: a little puff of dust behind their feet.
    step(x, y) {
        if (this.reduce || this.dust.length > 60) return
        this.dust.push({ x: x + (this.r() - 0.5) * 8, y: y + 14 + this.r() * 3, age: 0, life: 0.7 + this.r() * 0.3, s: 2 + this.r() * 2.5 })
    }

    arrive(x, y) {
        if (this.reduce) return
        this.poofs.push({ x, y, age: 0, life: 0.9 })
    }

    update(dt, { night, day }) {
        this.t += Math.max(0, dt)
        this.north.update(dt, { night, day, drift: this.windDrift() })
        this.south.update(dt, { night, day, drift: this.windDrift() })
        if (this.reduce) return
        const r = this.r
        const a = this.world.anchors
        const drift = this.windDrift()
        const advance = (list, fn) => { for (let i = list.length - 1; i >= 0; i--) { const p = list[i]; p.age += dt; if (p.age >= p.life) list.splice(i, 1); else fn(p) } }

        // smoke from the fire and the chimney
        const puff = (x, y, size, life) => this.smoke.push({ x, y, vx: drift * 0.3 + (r() - 0.5) * 6, vy: -16 - r() * 10, age: 0, life, s: size })
        if (a.fire && r() < dt * 3.2) puff(a.fire.x + (r() - 0.5) * 10, a.fire.y - 30, 5, 3.2)
        if (a.chimney && r() < dt * 1.6) puff(a.chimney.x + (r() - 0.5) * 4, a.chimney.y, 4, 3.8)
        if (a.cabinChimney && r() < dt * 1.4) puff(a.cabinChimney.x + (r() - 0.5) * 4, a.cabinChimney.y, 4, 3.8)
        if (a.caveFire && r() < dt * 2.4) puff(a.caveFire.x + (r() - 0.5) * 8, a.caveFire.y - 24, 4, 3)
        advance(this.smoke, (p) => { p.x += (p.vx + drift * 0.6 * (p.age / p.life)) * dt; p.y += p.vy * dt; p.vy *= 0.995 })

        // embers from the fire, busier after dark
        if (a.fire && r() < dt * (night ? 9 : 4)) this.embers.push({ x: a.fire.x + (r() - 0.5) * 18, y: a.fire.y - 14, vx: (r() - 0.5) * 16, vy: -40 - r() * 40, age: 0, life: 1 + r() * 1.2, phase: r() * TAU })
        advance(this.embers, (p) => { p.x += (p.vx + Math.sin(p.age * 6 + p.phase) * 12 + drift * 0.4) * dt; p.y += p.vy * dt; p.vy *= 0.98 })

        // music drifting out of the radio shack
        if (a.radio && r() < dt * 0.9) this.notes.push({ x: a.radio.x + (r() - 0.5) * 30, y: a.radio.y, age: 0, life: 3, glyph: r() < 0.5 ? "♪" : "♫", phase: r() * TAU })
        advance(this.notes, (p) => { p.x += (8 + Math.sin(p.age * 3 + p.phase) * 14) * dt; p.y -= 18 * dt })

        advance(this.dust, (p) => { p.y -= 4 * dt })
        advance(this.poofs, () => {})

        // the pond: ripples, glints, ducks and the odd fish
        const pond = this.world.pond
        const inPond = (k = 0.8) => { const ang = r() * TAU, d = Math.sqrt(r()) * k; return { x: pond.x + Math.cos(ang) * pond.rx * d, y: pond.y + Math.sin(ang) * pond.ry * d } }
        if (r() < dt * 0.8) this.ripples.push({ ...inPond(), age: 0, life: 2.4, s: 10 + r() * 10 })
        if (day && r() < dt * 5) this.glints.push({ ...inPond(0.85), age: 0, life: 0.6 + r() * 0.5 })
        advance(this.ripples, () => {})
        advance(this.glints, () => {})
        this.ducks.forEach((d) => { d.a += d.speed * dt; d.bob += dt * 2 })
        this.nextFish -= dt
        if (!this.fish && this.nextFish <= 0) {
            this.fish = { ...inPond(0.6), age: 0, life: 0.9, dir: r() < 0.5 ? -1 : 1 }
            this.nextFish = 9 + r() * 14
            this.ripples.push({ x: this.fish.x, y: this.fish.y, age: 0, life: 1.6, s: 8 })
        }
        if (this.fish) {
            this.fish.age += dt
            if (this.fish.age >= this.fish.life) {
                this.ripples.push({ x: this.fish.x + this.fish.dir * 26, y: this.fish.y, age: 0, life: 1.8, s: 12 })
                this.fish = null
            }
        }

        // butterflies flit between flowers, resting now and then
        this.butterflies.forEach((b) => {
            b.phase += dt * 16
            if (b.rest > 0) { b.rest -= dt; return }
            const dx = b.tx - b.x, dy = b.ty - b.y, d = Math.hypot(dx, dy)
            if (d < 6) {
                const next = this.spots[Math.floor(r() * this.spots.length)]
                b.tx = next.x; b.ty = next.y
                if (r() < 0.4) b.rest = 1 + r() * 3
                return
            }
            const speed = 38
            b.x += (dx / d) * speed * dt + Math.sin(this.t * 3 + b.phase) * 20 * dt
            b.y += (dy / d) * speed * dt + Math.cos(this.t * 4 + b.phase) * 24 * dt
        })

        // a flock crossing the sky every so often (by day)
        this.nextBirds -= dt
        if (!this.birds && this.nextBirds <= 0 && day) {
            const fromLeft = r() < 0.5
            const y0 = this.world.H * (0.15 + r() * 0.6)
            this.birds = {
                x: fromLeft ? -80 : this.world.W + 80, y: y0, vx: (fromLeft ? 1 : -1) * (150 + r() * 60), vy: (r() - 0.5) * 50,
                members: Array.from({ length: 3 + Math.floor(r() * 4) }, (_, i) => ({ dx: -i * 22 * (fromLeft ? 1 : -1) + (r() - 0.5) * 10, dy: (i % 2 ? 1 : -1) * i * 9, phase: r() * TAU }))
            }
            this.nextBirds = 22 + r() * 30
        }
        if (this.birds) {
            this.birds.x += this.birds.vx * dt
            this.birds.y += this.birds.vy * dt
            if (this.birds.x < -200 || this.birds.x > this.world.W + 200) this.birds = null
        }

        // fireflies wander near their home spot
        this.fireflies.forEach((f) => {
            f.phase += dt * f.speed
            f.x = f.hx + Math.sin(f.phase * 0.9) * 26 + Math.sin(f.phase * 2.1) * 8
            f.y = f.hy + Math.cos(f.phase * 0.7) * 18 + Math.sin(f.phase * 1.7) * 6
        })

        // sheep amble to a new patch now and then, and graze
        const pad = this.world.anchors.paddock
        this.sheep.forEach((sh) => {
            sh.bob += dt * 3
            if (sh.graze > 0) { sh.graze -= dt; return }
            const dx = sh.tx - sh.x, dy = sh.ty - sh.y, d = Math.hypot(dx, dy)
            if (d < 2) {
                const ang = r() * TAU, k = Math.sqrt(r())
                sh.tx = pad.x + Math.cos(ang) * pad.rx * k; sh.ty = pad.y + Math.sin(ang) * pad.ry * k
                sh.graze = 2 + r() * 6
                return
            }
            sh.facing = dx < 0 ? -1 : 1
            sh.x += (dx / d) * 14 * dt; sh.y += (dy / d) * 14 * dt
        })
        this.sailboats.forEach((b) => { b.phase += dt * 0.05 })

        // cloud shadows drift downwind and wrap around
        this.clouds.forEach((c) => {
            c.x += (drift * 0.9) * dt
            c.y += 2 * dt
            if (c.x - c.s > this.world.W) c.x = -c.s
            if (c.y - c.s > this.world.H) c.y = -c.s
        })
    }

    // --- drawing ---------------------------------------------------------------------

    // What is on screen (plus a margin), so a big map only draws what shows.
    setView(view) { this.view = view; this.north.setView(view); this.south.setView(view) }
    #visible(x, y, pad = 120) {
        const v = this.view
        return x > v.x - pad && x < v.x + v.w + pad && y > v.y - pad && y < v.y + v.h + pad
    }

    // Moving parts that belong to a building, sorted into the scene just in
    // front of it: the windmill's sails, the town hall clock's hands, the
    // market ticker, the film on the big screen.
    sceneItems(light) {
        const a = this.world.anchors, items = []
        if (a.windmill && this.#visible(a.windmill.x, a.windmill.y, 200)) items.push({ y: a.windmill.base + 0.5, draw: (ctx, pal) => this.#sails(ctx, pal, a.windmill) })
        if (a.clock && this.#visible(a.clock.x, a.clock.y, 200)) items.push({ y: a.clock.base + 0.5, draw: (ctx, pal) => this.#clock(ctx, pal, a.clock) })
        if (a.ticker && this.#visible(a.ticker.x, a.ticker.y, 200)) items.push({ y: a.ticker.base + 0.5, draw: (ctx) => this.#ticker(ctx, a.ticker) })
        if (a.screen && this.#visible(a.screen.x, a.screen.y, 200)) items.push({ y: a.screen.y + 100 + 0.5, draw: (ctx, pal) => this.drawFilm(ctx, pal, light) })
        if (a.ferrisWheel && this.#visible(a.ferrisWheel.x, a.ferrisWheel.y, 260)) items.push({ y: a.ferrisWheel.base - 0.5, draw: (ctx, pal) => this.#ferrisWheel(ctx, pal, a.ferrisWheel, light) })
        this.sheep.forEach((sh) => { if (this.#visible(sh.x, sh.y)) items.push({ y: sh.y, draw: (ctx, pal) => this.#sheep(ctx, pal, sh) }) })
        items.push(...this.north.sceneItems())
        return items
    }

    // The Ferris wheel, turning slowly, its gondolas hanging level.
    #ferrisWheel(ctx, pal, w, light) {
        const spin = this.reduce ? 0 : this.t * 0.12
        const ink = pal.ink, rim = pal.night ? "#8A8E8A" : "#F2EEE4"
        ctx.save()
        ctx.translate(w.x, w.y)
        ctx.strokeStyle = ink; ctx.lineWidth = 5; ctx.beginPath(); ctx.arc(0, 0, w.r, 0, TAU); ctx.stroke()
        ctx.strokeStyle = rim; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(0, 0, w.r, 0, TAU); ctx.stroke()
        ctx.strokeStyle = ink; ctx.lineWidth = 2.4; ctx.beginPath(); ctx.arc(0, 0, w.r * 0.62, 0, TAU); ctx.stroke()
        const cars = 10
        for (let i = 0; i < cars; i++) {
            const a = spin + (i / cars) * TAU
            line(ctx, [[0, 0], [Math.cos(a) * w.r, Math.sin(a) * w.r]], ink, 1.8)
        }
        for (let i = 0; i < cars; i++) {
            const a = spin + (i / cars) * TAU
            const x = Math.cos(a) * w.r, y = Math.sin(a) * w.r
            line(ctx, [[x, y], [x, y + 8]], ink, 1.2)
            ctx.beginPath(); ctx.moveTo(x - 9, y + 8); ctx.lineTo(x + 9, y + 8); ctx.lineTo(x + 7, y + 20); ctx.lineTo(x - 7, y + 20); ctx.closePath()
            ctx.fillStyle = pal.flag[i % pal.flag.length]; ctx.fill(); ctx.strokeStyle = ink; ctx.lineWidth = 1.2; ctx.stroke()
            if (light.lamps > 0.3) { ctx.fillStyle = `rgba(255,220,140,${0.9 * light.lamps})`; ctx.beginPath(); ctx.arc(x, y, 2.4, 0, TAU); ctx.fill() }
        }
        ctx.fillStyle = pal.metal; ctx.beginPath(); ctx.arc(0, 0, 8, 0, TAU); ctx.fill()
        ctx.strokeStyle = ink; ctx.lineWidth = 1.4; ctx.stroke()
        ctx.restore()
    }

    #sheep(ctx, pal, sh) {
        const grazing = sh.graze > 0
        const bob = this.reduce ? 0 : grazing ? 0 : Math.abs(Math.sin(sh.bob)) * -1.5
        const x = sh.x, y = sh.y + bob, f = sh.facing
        ctx.save()
        ctx.fillStyle = `rgba(${pal.shadow},0.2)`; ctx.beginPath(); ctx.ellipse(x + 2, sh.y + 2, 11, 3.5, 0, 0, TAU); ctx.fill()
        line(ctx, [[x - 5, y - 2], [x - 5, y + 2]], pal.ink, 1.8); line(ctx, [[x + 5, y - 2], [x + 5, y + 2]], pal.ink, 1.8)
        const wool = pal.night ? "#B8BCB4" : "#FBF8F0"
        ;[[-5, -7, 5], [0, -9, 6], [5, -7, 5], [-2, -5, 5], [3, -5, 5]].forEach(([dx, dy, r]) => {
            ctx.fillStyle = pal.ink; ctx.beginPath(); ctx.arc(x + dx, y + dy, r + 1.1, 0, TAU); ctx.fill()
        })
        ;[[-5, -7, 5], [0, -9, 6], [5, -7, 5], [-2, -5, 5], [3, -5, 5]].forEach(([dx, dy, r]) => {
            ctx.fillStyle = wool; ctx.beginPath(); ctx.arc(x + dx, y + dy, r, 0, TAU); ctx.fill()
        })
        const hx = x + f * 10, hy = y - (grazing ? 3 : 8)
        ctx.fillStyle = "#3A3432"; ctx.beginPath(); ctx.ellipse(hx, hy, 3.8, 3, 0, 0, TAU); ctx.fill()
        ctx.fillStyle = "#3A3432"; ctx.beginPath(); ctx.ellipse(hx - f * 2, hy - 3, 2, 1, f * 0.6, 0, TAU); ctx.fill()
        ctx.restore()
    }

    #sails(ctx, pal, hub) {
        const spin = this.reduce ? 0.4 : this.t * (0.5 + this.windDrift() / 40)
        ctx.save()
        ctx.translate(hub.x, hub.y)
        for (let i = 0; i < 4; i++) {
            ctx.save()
            ctx.rotate(spin + (i * Math.PI) / 2)
            ctx.scale(1, 0.92)
            line(ctx, [[0, 0], [0, -hub.r]], pal.ink, 4.5)
            line(ctx, [[0, 0], [0, -hub.r]], pal.woodDark, 2.6)
            ctx.fillStyle = pal.night ? "rgba(200,200,190,0.55)" : "rgba(250,247,239,0.92)"
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2
            ctx.beginPath(); ctx.rect(3, -hub.r, 20, hub.r - 16); ctx.fill(); ctx.stroke()
            for (let y = -hub.r + 12; y < -18; y += 12) line(ctx, [[3, y], [23, y]], pal.woodDark, 0.8, 0.8)
            line(ctx, [[13, -hub.r], [13, -16]], pal.woodDark, 0.8, 0.8)
            ctx.restore()
        }
        ctx.fillStyle = pal.woodDark; ctx.beginPath(); ctx.arc(0, 0, 6, 0, TAU); ctx.fill()
        ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
        ctx.restore()
    }

    // The town hall keeps the viewer's own time.
    #clock(ctx, pal, c) {
        const now = new Date()
        const h = (now.getHours() % 12) + now.getMinutes() / 60, m = now.getMinutes() + now.getSeconds() / 60
        const hand = (turns, len, width) => {
            const a = turns * TAU - Math.PI / 2
            line(ctx, [[c.x, c.y], [c.x + Math.cos(a) * len, c.y + Math.sin(a) * len]], pal.ink, width)
        }
        hand(h / 12, c.r * 0.55, 2.4)
        hand(m / 60, c.r * 0.8, 1.6)
        ctx.fillStyle = pal.ink; ctx.beginPath(); ctx.arc(c.x, c.y, 1.8, 0, TAU); ctx.fill()
    }

    #ticker(ctx, t) {
        const quotes = ["JANE ▲ 4.2%", "TEA ▲ 0.8%", "SOCKS ▼ 1.1%", "HOPE ▲ 12%", "NAPS ▲ 3.3%", "MONDAYS ▼ 2.0%", "PIE ▲ 7.5%"]
        const text = quotes.join("   ·   ") + "   ·   "
        ctx.save()
        ctx.beginPath(); ctx.rect(t.x, t.y, t.w, t.h); ctx.clip()
        ctx.font = "700 11px ui-monospace, Menlo, monospace"
        const width = ctx.measureText(text).width
        const offset = this.reduce ? 0 : (this.t * 30) % width
        ctx.textBaseline = "middle"
        let x = t.x - offset
        while (x < t.x + t.w) {
            quotes.forEach((q) => {
                const up = q.includes("▲")
                ctx.fillStyle = up ? "#7FE0A0" : "#F07A6A"
                ctx.fillText(q, x, t.y + t.h / 2)
                x += ctx.measureText(q + "   ·   ").width
            })
        }
        ctx.restore()
    }

    lilyBob(i) {
        return this.reduce ? 0 : Math.sin(this.t * 1.3 + i * 1.7) * 0.9
    }

    // On the water, under everything upright.
    drawWater(ctx, pal, light) {
        this.#waves(ctx, pal)
        this.north.drawWater(ctx, pal, light)
        this.south.drawWater(ctx, pal)
        this.sailboats.forEach((b) => { if (this.#visible(b.x, b.y, 160)) this.#sailboat(ctx, pal, b) })
        this.#river(ctx, pal)
        this.#koi(ctx, pal)
        this.boats.forEach((b) => { if (this.#visible(b.x, b.y)) this.#boat(ctx, pal, b) })
        const pond = this.world.pond
        if (!this.#visible(pond.x, pond.y, pond.rx + 60)) return
        ctx.save()
        ctx.beginPath(); ctx.ellipse(pond.x, pond.y, pond.rx, pond.ry, 0, 0, TAU); ctx.clip()
        ctx.strokeStyle = pal.foam
        this.ripples.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = (1 - k) * 0.7
            ctx.lineWidth = 1.3
            ctx.beginPath(); ctx.ellipse(p.x, p.y, p.s + k * 26, (p.s + k * 26) * 0.4, 0, 0, TAU); ctx.stroke()
        })
        if (light.day) {
            ctx.fillStyle = "#FFFFFF"
            this.glints.forEach((g) => {
                const k = Math.sin((g.age / g.life) * Math.PI)
                ctx.globalAlpha = k * 0.9
                const s = 2.5 * k
                ctx.beginPath(); ctx.moveTo(g.x - s * 2, g.y); ctx.lineTo(g.x, g.y - s * 0.6); ctx.lineTo(g.x + s * 2, g.y); ctx.lineTo(g.x, g.y + s * 0.6); ctx.closePath(); ctx.fill()
            })
        }
        ctx.globalAlpha = 1
        this.ducks.forEach((d) => this.#duck(ctx, pal, d))
        if (this.fish) this.#fish(ctx, pal)
        ctx.restore()
    }

    // Waves rolling in along the coast: foam lines that run up the sand and
    // fade, a few at a time along each stretch.
    #waves(ctx, pal) {
        const coast = this.world.anchors.coast
        if (!coast) return
        ctx.save()
        ctx.strokeStyle = pal.foam
        ctx.lineCap = "round"
        const t = this.reduce ? 0 : this.t
        for (let i = 0; i < coast.length - 6; i += 6) {
            const p = coast[i]
            if (!this.#visible(p.x, p.y, 80)) continue
            const q = coast[i + 6]
            for (let k = 0; k < 2; k++) {
                const phase = ((t * 0.18 + i * 0.013 + k * 0.5) % 1)
                const out = 60 * (1 - phase) - 4 // from offshore up to the tideline
                ctx.globalAlpha = Math.sin(phase * Math.PI) * 0.8
                ctx.lineWidth = 2.2
                ctx.beginPath()
                ctx.moveTo(p.x + p.nx * out, p.y + p.ny * out)
                ctx.quadraticCurveTo((p.x + q.x) / 2 + p.nx * (out + 6), (p.y + q.y) / 2 + p.ny * (out + 6), q.x + q.nx * out, q.y + q.ny * out)
                ctx.stroke()
            }
        }
        ctx.restore()
    }

    #sailboat(ctx, pal, b) {
        const t = this.reduce ? 0 : this.t
        const x = b.x + Math.sin(b.phase) * 120, y = b.y + Math.cos(b.phase * 0.7) * 30 + Math.sin(t * 1.2 + b.phase) * 1.5
        if (this.world.inSea && !this.world.inSea(x, y)) return
        ctx.save()
        ctx.translate(x, y)
        ctx.rotate(this.reduce ? 0 : Math.sin(t * 0.9 + b.phase) * 0.05)
        ctx.strokeStyle = pal.foam; ctx.globalAlpha = 0.6; ctx.lineWidth = 1.2
        ctx.beginPath(); ctx.moveTo(-22, 6); ctx.lineTo(-40, 10); ctx.moveTo(-22, 8); ctx.lineTo(-36, 14); ctx.stroke()
        ctx.globalAlpha = 1
        ctx.beginPath(); ctx.moveTo(-22, 0); ctx.lineTo(22, 0); ctx.lineTo(16, 9); ctx.lineTo(-16, 9); ctx.closePath()
        ctx.fillStyle = "#8A5A3A"; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
        line(ctx, [[0, 0], [0, -46]], pal.ink, 1.8)
        ctx.beginPath(); ctx.moveTo(2, -44); ctx.quadraticCurveTo(22, -24, 18, -3); ctx.lineTo(2, -3); ctx.closePath()
        ctx.fillStyle = pal.night ? "#B8BCB4" : "#FFFFFF"; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
        ctx.beginPath(); ctx.moveTo(-2, -38); ctx.lineTo(-2, -3); ctx.lineTo(-16, -3); ctx.closePath()
        ctx.fillStyle = pal.flag[0]; ctx.fill(); ctx.stroke()
        ctx.restore()
    }

    // Pale glints sliding downstream along the river.
    #river(ctx, pal) {
        const bridges = this.world.bridges || []
        ;(this.world.rivers || [this.world.river]).forEach((river) => { if (river) this.#flow(ctx, pal, river, bridges) })
    }

    #flow(ctx, pal, river, bridges) {
        ctx.save()
        ctx.strokeStyle = pal.foam
        ctx.lineWidth = 1.4
        const flow = this.reduce ? 0 : this.t * 1.6
        river.samples.forEach((p, i) => {
            if (i % 4 || !this.#visible(p.x, p.y, 40)) return
            if (bridges.some((b) => Math.hypot((b.x1 + b.x2) / 2 - p.x, (b.y1 + b.y2) / 2 - p.y) < 40)) return
            const k = ((i * 0.37 + flow) % 1)
            const o = (Math.sin(i * 1.7) * 0.5) * river.width * 0.6
            const along = (k - 0.5) * 20
            ctx.globalAlpha = Math.sin(k * Math.PI) * 0.7
            const x = p.x + p.nx * o + p.ny * along, y = p.y + p.ny * o - p.nx * along
            ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x - p.ny * 8, y + p.nx * 8); ctx.stroke()
        })
        ctx.restore()
    }

    #koi(ctx, pal) {
        const pond = this.world.anchors.koi
        if (!pond || !this.#visible(pond.x, pond.y)) return
        this.koi.forEach((k) => {
            const a = this.reduce ? k.a : k.a + this.t * k.speed
            const x = pond.x + Math.cos(a) * pond.rx * k.k, y = pond.y + Math.sin(a) * pond.ry * k.k
            const heading = a + Math.PI / 2
            ctx.save()
            ctx.translate(x, y)
            ctx.rotate(Math.atan2(Math.cos(heading) * pond.ry, -Math.sin(heading) * pond.rx) + Math.PI / 2)
            ctx.fillStyle = k.color
            ctx.beginPath(); ctx.ellipse(0, 0, 6, 2.6, 0, 0, TAU); ctx.fill()
            ctx.beginPath(); ctx.moveTo(-5, 0); ctx.lineTo(-10, -3 + Math.sin(this.t * 8) * 1.5); ctx.lineTo(-10, 3 + Math.sin(this.t * 8) * 1.5); ctx.closePath(); ctx.fill()
            ctx.restore()
        })
    }

    #boat(ctx, pal, b) {
        const drift = b.drift && !this.reduce ? { x: Math.sin(this.t * 0.05 + b.phase) * 60, y: Math.cos(this.t * 0.04 + b.phase) * 24 } : { x: 0, y: 0 }
        const bob = this.reduce ? 0 : Math.sin(this.t * 1.4 + b.phase) * 1.2
        const x = b.x + drift.x, y = b.y + drift.y + bob
        ctx.save()
        ctx.translate(x, y)
        ctx.rotate(this.reduce ? 0 : Math.sin(this.t * 0.9 + b.phase) * 0.04)
        ctx.strokeStyle = pal.foam; ctx.globalAlpha = 0.5; ctx.lineWidth = 1.2
        ctx.beginPath(); ctx.ellipse(0, 4, 30, 6, 0, 0, TAU); ctx.stroke()
        ctx.globalAlpha = 1
        ctx.beginPath(); ctx.moveTo(-26, -2); ctx.quadraticCurveTo(0, 12, 26, -2); ctx.lineTo(22, -8); ctx.lineTo(-22, -8); ctx.closePath()
        ctx.fillStyle = b.color; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.4; ctx.stroke()
        ctx.beginPath(); ctx.ellipse(0, -8, 22, 5, 0, 0, TAU); ctx.fillStyle = pal.woodLight; ctx.fill(); ctx.stroke()
        line(ctx, [[-8, -12], [-8, -4]], pal.woodDark, 2); line(ctx, [[8, -12], [8, -4]], pal.woodDark, 2)
        line(ctx, [[-18, -6], [-34, 2]], pal.woodDark, 1.8); line(ctx, [[18, -6], [34, 2]], pal.woodDark, 1.8)
        ctx.restore()
    }

    #duck(ctx, pal, d) {
        const pond = this.world.pond
        const x = pond.x + Math.cos(d.a) * d.rx, y = pond.y + Math.sin(d.a) * d.ry + Math.sin(d.bob) * 0.8
        const facing = -Math.sin(d.a) > 0 ? 1 : -1 // which way it swims along the loop
        // wake
        ctx.strokeStyle = pal.foam; ctx.lineWidth = 1.1; ctx.globalAlpha = 0.6
        ctx.beginPath(); ctx.moveTo(x - facing * 6, y + 2); ctx.lineTo(x - facing * 22, y - 3); ctx.moveTo(x - facing * 6, y + 3); ctx.lineTo(x - facing * 22, y + 8); ctx.stroke()
        ctx.globalAlpha = 1
        const body = d.color === "white" ? "#F7F4EA" : "#9C7A58"
        ctx.fillStyle = body; ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2
        ctx.beginPath(); ctx.ellipse(x, y, 10, 6, 0, 0, TAU); ctx.fill(); ctx.stroke()
        ctx.beginPath(); ctx.moveTo(x - facing * 9, y - 1); ctx.lineTo(x - facing * 13, y - 5); ctx.lineTo(x - facing * 7, y - 3); ctx.closePath(); ctx.fill(); ctx.stroke()
        ctx.fillStyle = d.color === "white" ? "#F7F4EA" : "#3F6E4E"
        ctx.beginPath(); ctx.arc(x + facing * 7, y - 7, 4.5, 0, TAU); ctx.fill(); ctx.stroke()
        ctx.fillStyle = "#F2A33A"
        ctx.beginPath(); ctx.moveTo(x + facing * 11, y - 8); ctx.lineTo(x + facing * 16, y - 6.5); ctx.lineTo(x + facing * 11, y - 5.5); ctx.closePath(); ctx.fill()
        ctx.fillStyle = pal.ink; ctx.beginPath(); ctx.arc(x + facing * 8.5, y - 8, 0.9, 0, TAU); ctx.fill()
    }

    #fish(ctx, pal) {
        const f = this.fish, k = f.age / f.life
        const x = f.x + f.dir * 26 * k, y = f.y - Math.sin(k * Math.PI) * 22
        ctx.save()
        ctx.translate(x, y)
        ctx.rotate(f.dir * (k - 0.5) * 2)
        ctx.fillStyle = "#E8894A"; ctx.strokeStyle = pal.ink; ctx.lineWidth = 1
        ctx.beginPath(); ctx.ellipse(0, 0, 7, 3.4, 0, 0, TAU); ctx.fill(); ctx.stroke()
        ctx.beginPath(); ctx.moveTo(-f.dir * 6, 0); ctx.lineTo(-f.dir * 11, -3.5); ctx.lineTo(-f.dir * 11, 3.5); ctx.closePath(); ctx.fill(); ctx.stroke()
        ctx.restore()
        ctx.fillStyle = "rgba(255,255,255,0.8)"
        ;[-1, 1].forEach((s) => { ctx.beginPath(); ctx.arc(x + s * 4, y + 4 + k * 3, 1.2, 0, TAU); ctx.fill() })
    }

    // On the ground, under walkers: dust and arrival puffs.
    drawGroundLife(ctx, pal) {
        ctx.save()
        ctx.fillStyle = pal.pathLight
        this.dust.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = (1 - k) * 0.55
            ctx.beginPath(); ctx.arc(p.x, p.y, p.s + k * 4, 0, TAU); ctx.fill()
        })
        this.poofs.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = (1 - k) * 0.8
            ctx.strokeStyle = "#E0A43A"; ctx.lineWidth = 2.5 * (1 - k) + 0.5
            ctx.beginPath(); ctx.ellipse(p.x, p.y + 12, 10 + k * 34, (10 + k * 34) * 0.4, 0, 0, TAU); ctx.stroke()
            for (let i = 0; i < 6; i++) {
                const a = (i / 6) * TAU
                ctx.fillStyle = pal.pathLight
                ctx.beginPath(); ctx.arc(p.x + Math.cos(a) * (12 + k * 26), p.y + 10 + Math.sin(a) * (5 + k * 10) - k * 8, 3 * (1 - k) + 1, 0, TAU); ctx.fill()
            }
        })
        ctx.restore()
    }

    // The film playing on the big screen — sorted into the scene just above
    // the screen itself, so anything standing in front still covers it.
    drawFilm(ctx, pal, light) {
        const s = this.world.anchors.screen
        if (!s) return
        const t = this.reduce ? 0 : Math.max(0, this.t)
        ctx.save()
        ctx.beginPath(); ctx.rect(s.x, s.y, s.w, s.h); ctx.clip()
        ctx.globalAlpha = light.lamps > 0.5 ? 0.85 : 0.55
        const scene = Math.abs(Math.floor(t / 6)) % 3
        const skies = [["#F6C177", "#EB6F92"], ["#9CCFD8", "#3E8FB0"], ["#C4A7E7", "#31748F"]][scene]
        const g = ctx.createLinearGradient(0, s.y, 0, s.y + s.h)
        g.addColorStop(0, skies[0]); g.addColorStop(1, skies[1])
        ctx.fillStyle = g; ctx.fillRect(s.x, s.y, s.w, s.h)
        // rolling hills and a little figure walking across
        ctx.fillStyle = "rgba(40,60,50,0.55)"
        ctx.beginPath(); ctx.moveTo(s.x, s.y + s.h)
        for (let x = 0; x <= s.w; x += 8) ctx.lineTo(s.x + x, s.y + s.h * 0.7 + Math.sin((x + t * 12) * 0.05) * 6)
        ctx.lineTo(s.x + s.w, s.y + s.h); ctx.closePath(); ctx.fill()
        const fx = s.x + ((t * 14) % (s.w + 20)) - 10
        ctx.fillStyle = "rgba(20,24,28,0.8)"
        ctx.beginPath(); ctx.arc(fx, s.y + s.h * 0.55, 3, 0, TAU); ctx.fill()
        ctx.fillRect(fx - 1.5, s.y + s.h * 0.58, 3, 7)
        ctx.fillStyle = "rgba(255,255,255,0.9)"
        ctx.beginPath(); ctx.arc(s.x + s.w * 0.78, s.y + s.h * 0.28, 5, 0, TAU); ctx.fill()
        ctx.restore()
    }

    // In the air, over everything: smoke, music, butterflies, and birds with
    // their shadows. (Things that give off light come later, in drawLights.)
    drawAir(ctx, pal, light) {
        ctx.save()
        // smoke
        this.smoke.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = Math.sin(k * Math.PI) * (pal.night ? 0.22 : 0.3)
            ctx.fillStyle = pal.night ? "#8A969A" : "#F4F2EC"
            ctx.beginPath(); ctx.arc(p.x, p.y, p.s + k * 16, 0, TAU); ctx.fill()
        })
        // music notes
        ctx.fillStyle = pal.label
        ctx.font = "700 16px system-ui, sans-serif"
        ctx.textAlign = "center"
        this.notes.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = Math.sin(k * Math.PI) * 0.75
            ctx.fillText(p.glyph, p.x, p.y)
        })
        ctx.globalAlpha = 1
        this.#balloons(ctx, pal)
        this.#anemometer(ctx, pal)
        this.north.drawAir(ctx, pal, light)
        this.south.drawAir(ctx, pal, light)
        // butterflies
        if (light.day) this.butterflies.forEach((b) => {
            if (!this.#visible(b.x, b.y)) return
            const flap = b.rest > 0 ? 0.35 + Math.abs(Math.sin(b.phase * 0.2)) * 0.3 : Math.abs(Math.sin(b.phase))
            ctx.fillStyle = b.color; ctx.strokeStyle = pal.ink; ctx.lineWidth = 0.8
            ;[-1, 1].forEach((side) => {
                ctx.beginPath(); ctx.ellipse(b.x + side * 3.2 * flap, b.y - 8, 3.4 * flap + 0.4, 4.2, side * 0.4, 0, TAU); ctx.fill(); ctx.stroke()
            })
            ctx.fillStyle = pal.ink; ctx.fillRect(b.x - 0.6, b.y - 11, 1.2, 6)
        })
        // a flock and its shadows
        if (this.birds) {
            const flock = this.birds
            flock.members.forEach((m) => {
                const x = flock.x + m.dx, y = flock.y + m.dy
                ctx.fillStyle = `rgba(${pal.shadow},0.16)`
                ctx.beginPath(); ctx.ellipse(x + 50, y + 90, 7, 2.5, 0, 0, TAU); ctx.fill()
                const flap = Math.sin(this.t * 11 + m.phase) * 5
                ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6
                ctx.beginPath(); ctx.moveTo(x - 7, y - flap); ctx.quadraticCurveTo(x - 3, y - 3, x, y); ctx.quadraticCurveTo(x + 3, y - 3, x + 7, y - flap); ctx.stroke()
            })
        }
        ctx.restore()
    }

    // Light itself, added on top after the hour's tint so it glows through
    // the dark: embers, fireflies, the stage's beams, the projector, the
    // cinema's string lights.
    drawLights(ctx, pal, light) {
        const a = this.world.anchors
        ctx.save()
        ctx.globalCompositeOperation = "lighter"
        this.embers.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = (1 - k) * 0.9
            ctx.drawImage(this.glow, p.x - 6, p.y - 6, 12, 12)
        })
        if (light.lamps > 0.5) {
            this.fireflies.forEach((f) => {
                if (!this.#visible(f.x, f.y)) return
                const b = Math.max(0, Math.sin(this.t * f.blink + f.phase * 3))
                if (b < 0.05) return
                ctx.globalAlpha = b * 0.9 * light.lamps
                ctx.drawImage(this.glow, f.x - 7, f.y - 7, 14, 14)
            })
        }
        if (a.spotlights && a.stage) {
            a.spotlights.forEach((sp, i) => {
                const sweep = this.reduce ? 0 : Math.sin(this.t * 0.6 + i * 2) * 60
                const tx = a.stage.x + sweep, ty = a.stage.y
                const beam = ctx.createLinearGradient(sp.x, sp.y, tx, ty)
                beam.addColorStop(0, `rgba(255,236,180,${0.28 * (0.4 + light.lamps * 0.6)})`)
                beam.addColorStop(1, "rgba(255,236,180,0)")
                ctx.globalAlpha = 1
                ctx.fillStyle = beam
                ctx.beginPath(); ctx.moveTo(sp.x, sp.y); ctx.lineTo(tx - 34, ty + 8); ctx.lineTo(tx + 34, ty - 8); ctx.closePath(); ctx.fill()
            })
        }
        if (a.projector && a.screen && light.lamps > 0.3) {
            const beam = ctx.createLinearGradient(a.projector.x, a.projector.y, a.screen.x + a.screen.w / 2, a.screen.y + a.screen.h / 2)
            beam.addColorStop(0, `rgba(210,225,255,${0.22 * light.lamps})`)
            beam.addColorStop(1, "rgba(210,225,255,0.02)")
            ctx.globalAlpha = 1
            ctx.fillStyle = beam
            ctx.beginPath(); ctx.moveTo(a.projector.x - 4, a.projector.y); ctx.lineTo(a.screen.x + 10, a.screen.y + a.screen.h); ctx.lineTo(a.screen.x + a.screen.w - 10, a.screen.y + a.screen.h); ctx.lineTo(a.projector.x + 4, a.projector.y); ctx.closePath(); ctx.fill()
        }
        if (a.beacon && light.lamps > 0.3 && this.#visible(a.beacon.x, a.beacon.y, 700)) {
            // the lighthouse lamp sweeps round
            const ang = this.reduce ? 0.6 : this.t * 0.7
            const len = 620, spread = 0.12
            const beam = ctx.createRadialGradient(a.beacon.x, a.beacon.y, 4, a.beacon.x, a.beacon.y, len)
            beam.addColorStop(0, `rgba(255,236,170,${0.5 * light.lamps})`)
            beam.addColorStop(1, "rgba(255,236,170,0)")
            ctx.globalAlpha = 1
            ctx.fillStyle = beam
            ctx.beginPath(); ctx.moveTo(a.beacon.x, a.beacon.y)
            ctx.arc(a.beacon.x, a.beacon.y, len, ang - spread, ang + spread); ctx.closePath(); ctx.fill()
            ctx.globalAlpha = light.lamps
            ctx.drawImage(this.glow, a.beacon.x - 24, a.beacon.y - 24, 48, 48)
        }
        if (a.crystalBall && this.#visible(a.crystalBall.x, a.crystalBall.y)) {
            ctx.globalAlpha = 0.45 + 0.35 * Math.sin(this.t * 2.3)
            ctx.drawImage(this.glow, a.crystalBall.x - 14, a.crystalBall.y - 14, 28, 28)
        }
        this.north.drawLights(ctx, pal, light)
        this.south.drawLights(ctx, pal, light)
        ctx.globalCompositeOperation = "lighter"
        if (a.stringLights && light.lamps > 0.3) {
            a.stringLights.forEach((b, i) => {
                const tw = 0.6 + 0.4 * Math.sin(this.t * 3 + i * 1.3)
                ctx.globalAlpha = tw * light.lamps
                ctx.drawImage(this.glow, b.x - 9, b.y - 9, 18, 18)
            })
        }
        ctx.restore()
    }

    #balloons(ctx, pal) {
        const balloons = this.world.anchors.balloons
        if (!balloons || !balloons.length || !this.#visible(balloons[0].x, balloons[0].y, 250)) return
        const colors = ["#E8543A", "#F2C14E", "#6FB3A8", "#8E7CC3", "#EE9AA8"]
        balloons.forEach((b, i) => {
            const sway = this.reduce ? 0 : Math.sin(this.t * 1.1 + i * 1.3) * 5
            const bob = this.reduce ? 0 : Math.sin(this.t * 1.7 + i) * 3
            const x = b.x + sway, y = b.y + bob
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 0.8; ctx.globalAlpha = 0.7
            ctx.beginPath(); ctx.moveTo(b.x - sway * 0.2, b.anchorY); ctx.quadraticCurveTo(x - 4, (y + b.anchorY) / 2, x, y + 12); ctx.stroke()
            ctx.globalAlpha = 1
            ctx.fillStyle = colors[b.color % colors.length]
            ctx.beginPath(); ctx.ellipse(x, y, 9, 11, 0, 0, TAU); ctx.fill()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
            ctx.fillStyle = "rgba(255,255,255,0.45)"; ctx.beginPath(); ctx.ellipse(x - 3, y - 4, 2.5, 3.5, -0.4, 0, TAU); ctx.fill()
        })
    }

    #anemometer(ctx, pal) {
        const a = this.world.anchors.anemometer
        if (!a || !this.#visible(a.x, a.y)) return
        const spin = this.reduce ? 0 : this.t * (2 + this.windDrift() / 8)
        for (let i = 0; i < 3; i++) {
            const ang = spin + (i * TAU) / 3
            const x = a.x + Math.cos(ang) * 14, y = a.y + Math.sin(ang) * 5
            line(ctx, [[a.x, a.y], [x, y]], pal.metal, 1.4)
            ctx.fillStyle = pal.metal; ctx.beginPath(); ctx.arc(x, y, 3.2, 0, TAU); ctx.fill()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 0.8; ctx.stroke()
        }
    }

    // Soft cloud shadows sliding over the meadow (daylight only).
    drawClouds(ctx, light) {
        if (!light.day) return
        ctx.save()
        ctx.globalAlpha = 0.12
        this.clouds.forEach((c) => { if (this.#visible(c.x, c.y, c.s)) ctx.drawImage(this.cloudCanvas, c.x - c.s, c.y - c.s * c.squash, c.s * 2, c.s * 2 * c.squash) })
        ctx.restore()
    }

    // The hour's color over the whole view.
    drawTint(ctx, light, view) {
        if (!light.tint) return
        const [rgb, alpha] = light.tint
        ctx.save()
        ctx.globalCompositeOperation = "multiply"
        ctx.fillStyle = `rgba(${rgb},${alpha * 2.2})`
        ctx.fillRect(view.x - 10, view.y - 10, view.w + 20, view.h + 20)
        ctx.restore()
    }
}

function makeGlow() {
    const c = document.createElement("canvas")
    c.width = c.height = 32
    const g = c.getContext("2d")
    const grad = g.createRadialGradient(16, 16, 0, 16, 16, 16)
    grad.addColorStop(0, "rgba(255,240,170,1)")
    grad.addColorStop(0.25, "rgba(255,200,90,0.8)")
    grad.addColorStop(1, "rgba(255,160,60,0)")
    g.fillStyle = grad
    g.fillRect(0, 0, 32, 32)
    return c
}

function makeCloud() {
    const c = document.createElement("canvas")
    c.width = c.height = 256
    const g = c.getContext("2d")
    const blob = (x, y, r) => {
        const grad = g.createRadialGradient(x, y, 0, x, y, r)
        grad.addColorStop(0, "rgba(20,35,40,0.9)")
        grad.addColorStop(1, "rgba(20,35,40,0)")
        g.fillStyle = grad
        g.beginPath(); g.arc(x, y, r, 0, TAU); g.fill()
    }
    blob(128, 128, 110); blob(80, 120, 70); blob(176, 116, 76); blob(120, 90, 60)
    return c
}
